import { ApolloLink } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient } from '@apollo/client/core';
import { useCallback, useMemo, useRef } from 'react';

import attachClientToStore from '@/Apollo/attachClientToStore';
import { clearCartDataFromCache } from '@/Apollo/clearCartDataFromCache';
import { clearCustomerDataFromCache } from '@/Apollo/clearCustomerDataFromCache';
import { CACHE_PERSIST_PREFIX } from '@/Apollo/constants';
import getLinks from '@/Apollo/links';
import typePolicies from '@/Apollo/policies';
import { websiteEnv } from '@/constants/website-env';
import store from '@/store';
import { BrowserPersistence } from '@/util';

const isServer = !globalThis.document;
const storage = new BrowserPersistence();

const configureLinks = links => [...links.values()];

const styles = new Set();

export const useAdapter = () => {
  const origin = globalThis.location?.origin;
  const isInitRef = useRef(true);

  const baseUrl = isServer
    ? `${process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL}graphql`
    : `${process.env.NEXT_PUBLIC_BASE_PATH}/api/graphql`;

  const apiBase = useMemo(() => new URL(baseUrl, origin).toString(), [baseUrl, origin]);

  const apolloLink = useMemo(() => {
    let links = getLinks(apiBase);

    if (configureLinks) {
      links = configureLinks(links, apiBase);
    }

    return ApolloLink.from(Array.from(links.values()));
  }, [apiBase]);

  const createApolloClient = useCallback((cache, link) => {
    return new ApolloClient({
      cache,
      link,
      ssrMode: isServer,
    });
  }, []);

  const clearCacheData = useCallback(
    async (client, cacheType) => {
      const storeCode = storage.getItem('store_view_code') || 'default';

      // Clear current store
      if (cacheType === 'cart') {
        await clearCartDataFromCache(client);
      } else if (cacheType === 'customer') {
        await clearCustomerDataFromCache(client);
      }

      // Clear other stores
      for (const store of JSON.parse(process.env.NEXT_PUBLIC_AVAILABLE_STORE_VIEWS)) {
        if (store.store_code !== storeCode) {
          // Get saved data directly from local storage
          const existingStorePersistor = storage.getItem(`${CACHE_PERSIST_PREFIX}-${store.store_code}`);

          // Make sure we have data available
          if (existingStorePersistor && Object.keys(existingStorePersistor).length > 0) {
            const storeCache = new InMemoryCache({
              typePolicies,
            });

            // Restore available data
            storeCache.restore(JSON.parse(existingStorePersistor));

            const storeClient = createApolloClient(storeCache, apolloLink);

            // Clear other store
            if (cacheType === 'cart') {
              await clearCartDataFromCache(storeClient);
            } else if (cacheType === 'customer') {
              await clearCustomerDataFromCache(storeClient);
            }
          }
        }
      }
    },
    [apolloLink, createApolloClient],
  );

  const apolloClient = useMemo(() => {
    const client = createApolloClient(preInstantiatedCache, apolloLink);

    client.apiBase = apiBase;
    client.clearCacheData = clearCacheData;

    return client;
  }, [apiBase, apolloLink, clearCacheData, createApolloClient]);

  const apolloProps = {
    client: apolloClient,
  };
  const reduxProps = {
    store,
  };

  const styleProps = {
    initialState: styles,
  };

  if (isInitRef.current) {
    isInitRef.current = false;
    attachClientToStore(apolloClient);
  }

  return {
    apolloProps,
    reduxProps,
    styleProps,
  };
};

/**
 * To improve initial load time, create an apollo cache object as soon as
 * this module is executed, since it doesn't depend on any component props.
 * The tradeoff is that we may be creating an instance we don't end up needing.
 */
const preInstantiatedCache = new InMemoryCache(
  Object.assign(
    {
      possibleTypes: JSON.parse(process.env.NEXT_PUBLIC_POSSIBLE_TYPES),
      typePolicies,
    },
    websiteEnv.isAustralia ||
      websiteEnv.isIreland ||
      websiteEnv.isNewZealand ||
      websiteEnv.isGermanyEnglish ||
      websiteEnv.isSingapore
      ? {
          dataIdFromObject: object => {
            // Customize the cache key based on the site
            if (object.__typename && (object.id || object._id)) {
              return `${object.__typename}:${object.id || object._id}:${process.env.NEXT_PUBLIC_STORE_VIEW_CODE}`;
            }

            return null;
          },
        }
      : {},
  ),
);
